/** Which documents are still moving, how often to ask, and what to call each state. */
import type { Doc } from "../api/api";
import type { Rules } from "./useTransientPolling";
import { rejectMessage } from "../text/errors";

export function isTransient(d: Doc): boolean {
  if (d.status === "pending") return true;
  if (d.status !== "uploaded" || d.kind !== "textbook") return false;
  return (
    d.book_status === null ||
    d.book_status === "detecting" ||
    d.book_status === "confirmed" ||
    d.book_status === "ingesting"
  );
}

/** Upload and detection settle in seconds; an ingest takes minutes. */
export function intervalFor(d: Doc): number {
  return d.book_status === "confirmed" || d.book_status === "ingesting" ? 5000 : 2000;
}

export const DOC_RULES: Rules<Doc> = { moving: isTransient, interval: intervalFor };

function minutesLeft(seconds: number): string {
  const m = Math.max(1, Math.ceil(seconds / 60));
  return `about ${m} ${m === 1 ? "minute" : "minutes"} left`;
}

function ingesting(d: Doc): string {
  const p = d.progress;
  if (!p) return "making your flashcards…";
  if (p.chapters_done === 0) return `making your flashcards… ${minutesLeft(p.eta_seconds)}`;
  const done = `${p.chapters_done} of ${p.chapters_total} chapters done`;
  const rest = p.chapters_done >= p.chapters_total ? "finishing up" : minutesLeft(p.eta_seconds);
  return `making your flashcards · ${done} · ${rest}`;
}

export function describeDoc(d: Doc): string {
  const kind = d.kind === "textbook" ? "Textbook" : "Past paper";
  if (d.status === "rejected") return `${kind} · rejected: ${rejectMessage(d.reject_reason)}`;
  if (d.status !== "uploaded") return `${kind} · ${d.status}`;
  const pages = `${kind} · ${d.page_count} pages`;
  if (d.kind !== "textbook") return pages;
  switch (d.book_status) {
    case null:
    case "detecting":
      return `${pages} · finding chapters…`;
    case "review":
      return `${pages} · chapters need checking`;
    case "confirmed":
      return `${pages} · queued — your flashcards are on the way`;
    case "ingesting":
      return `${pages} · ${ingesting(d)}`;
    case "ready":
      return `${pages} · ready to study`;
    case "failed":
      return `${pages} · needs attention — check the chapters and try again`;
  }
}
